import { useEffect, useState } from 'react';

import styled, { css } from 'styled-components';
import {
  flexBoxCenter,
  flexDirectionColumn,
  justifyContentFlexStart,
  justifyContentSpaceBetween,
  layerA,
  layerA180Deg,
  layerB,
  layerBDark,
  layerCLighter,
} from 'shared/styles/commonStyles';

import ScheduleCalendar from 'features/masterControl/components/controls/heatingScheduler/ScheduleCalendar';
import DisplayConsumptionBox from './DisplayConsumptionBox';
import DisplayFaultsTimer from './DisplayFaultsTimer';
import DisplayGraphInfoTitle from './DisplayGraphInfoTitle';
import DisplayLegend from './DisplayLegend';
import ChartContainer from './ChartContainer';
import { useDispatch, useSelector } from 'react-redux';
import { getDataConsumptionService, getTelemetryService } from 'shared/services';
import { formatDataConsumptionTotal, formatTime, getGraphTypeKey } from 'shared/helpers/helpers';
import {
  handleEssGraphDate,
  selectEssSwitch,
} from 'shared/slice/essSwitchSlice';
import {
  handleTgsGraphDate,
  selectTgsSwitch,
} from 'shared/slice/tgsSwitchSlice';
import {
  handleTesGraphDate,
  selectTesSwitch,
} from 'shared/slice/tesSwitchSlice';
import { useLocations } from 'shared/hooks';
import { useChartDrilldown } from './chartControls/useChartDrilldown';

const GraphMain = ({
  swtName,
  location,
  machine,
  setAuditLogData,
  setIsReportReady,
  handleMessage,
}) => {
  const dispatch = useDispatch();

  const locations = useLocations();

  const energySource =
    swtName === 'tgs'
      ? 'gas consumption vs time'
      : 'energy consumption vs time';
  const graphSelection = [
    'heater temp. vs real time',
    'heater temp. vs time',
    energySource,
    'data consumption vs time',
  ];

  const selectButtons = ['clear', 'apply'];
  // the chart renders the real time graph on open, so the select box has to
  // come up on the matching option instead of an empty placeholder
  const [selectedOne, setSelectedOne] = useState(graphSelection[0]);
  const [isSelected, setIsSelected] = useState([true, false, false, false]);
  const [openGraphSelect, setOpenGraphSelect] = useState(false);
  const [openDateSelect, setOpenDateSelect] = useState(false);
  const [date, setDate] = useState({
    start: { date: null, time: null },
    end: { date: null, time: null },
  });
  const [isReadyToRender, setIsReadyToRender] = useState(false);
  // The range currently plotted, which drilling changes and the date picker
  // does not. drillInto must stack this, not the picker range, or every level
  // pushes the same value and drill-out skips straight to the top.
  const [displayedRange, setDisplayedRange] = useState(null);

  // Drill-down re-runs the same search over a narrower range. It lives here
  // because this component owns the date state and the fetch; the charts
  // themselves only report which bar was clicked.
  const drilldown = useChartDrilldown({
    onRangeChange: (range) => handleSearchButton(range),
  });

  const { flatEssSwitch, flatTgsSwitch, flatTesSwitch } = useSelector(
    swtName === 'ess'
      ? selectEssSwitch
      : swtName === 'tgs'
        ? selectTgsSwitch
        : selectTesSwitch,
  );
  const swtStatus =
    swtName === 'ess'
      ? flatEssSwitch
      : swtName === 'tgs'
        ? flatTgsSwitch
        : flatTesSwitch;
  // socket and SSR-spec reducers can create a machine entry before any graph
  // data has landed on it, so graphDateData is not guaranteed here the way it
  // is inside BarGraph, which only renders after a search
  const { intervalUnit } = swtStatus[location][machine].graphDateData ?? {};

  // Drilling needs a range to return to. When the date picker has been
  // cleared there is nothing to go back to, so clicking a stale bar must do
  // nothing rather than fire a search that quietly fetches nothing.
  const currentRange =
    date.start.date && date.end.date
      ? {
          startDate: formatTime(date.start, true),
          endDate: formatTime(date.end, true),
        }
      : null;

  const handlePointClick = (timestamp) => {
    const rangeOnScreen = displayedRange ?? currentRange;

    if (rangeOnScreen) {
      drilldown.drillInto(timestamp, intervalUnit, rangeOnScreen);
    }
  };

  const { start, end } = date;
  const displayStart =
    start.date !== null
      ? `${start.time.hour} : ${start.time.minute} ${
          start.time.division
        } - ${start.date.getDate()} / ${
          start.date.getMonth() + 1
        } / ${start.date.getFullYear()} `
      : ' -----------------';

  const displayEnd =
    end.date !== null
      ? `${end.time.hour} : ${end.time.minute} ${
          end.time.division
        } - ${end.date.getDate()} / ${
          end.date.getMonth() + 1
        } / ${end.date.getFullYear()} `
      : ' -----------------';







  useEffect(() => {
    setAuditLogData({});
    // the real time graph is always on screen, the others need a search first
    setIsReportReady(isSelected[0]);
    // a drill stack from one series means nothing on another
    drilldown.reset();
  }, [selectedOne, date, isSelected]);


  // rangeOverride is supplied by drill-down. The Search button passes its
  // click event as the first argument, so only an object carrying a numeric
  // startDate counts as a real override.
  const handleSearchButton = (rangeOverride) => {
    const override =
      typeof rangeOverride?.startDate === 'number' ? rangeOverride : null;
    const deviceIds = [machine];
    const isRealTimeGraph = isSelected[0];
    const hasSelectedGraph = isSelected.some((el) => el);
    const hasDateRange =
      date.start.date && date.start.time && date.end.date && date.end.time;

    if (
      !override &&
      (!hasSelectedGraph || (!isRealTimeGraph && !hasDateRange))
    ) {
      handleMessage('open');
      return Promise.resolve();
    }

    const startDate = override
      ? override.startDate
      : hasDateRange
        ? formatTime(date.start, true)
        : null;
    const endDate = override
      ? override.endDate
      : hasDateRange
        ? formatTime(date.end, true)
        : null;

    // The real time graph searches with no range at all, and drilling has
    // nothing to return to in that case.
    if (startDate) {
      setDisplayedRange({ startDate, endDate });
    }

    // Drill-down awaits this to know whether the refetch succeeded.
    const requests = [];

    const handleTelemetryData = (data, unit, type) => {
      const newData = {
        location,
        machine,
        graphType: getGraphTypeKey(type),
        data: data[0]?.data
          .filter((el) => typeof el.startTime === 'number')
          .map((el) => {
            return [
              el.startTime,
              type === 'fuel' || type === 'energy'
                ? el.consumption
                : el.avg_value,
            ];
          }),
        unit,
      };
      if (swtName === 'ess') {
        dispatch(handleEssGraphDate(newData));
      } else if (swtName === 'tgs') {
        dispatch(handleTgsGraphDate(newData));
      } else if (swtName === 'tes') {
        dispatch(handleTesGraphDate(newData));
      }
    };

    const getAuditLogData = (data, isDc) => {
      const totalUsageHours = Math.floor(
        data?.reduce((acc, cur) => {
          return acc + ((isDc ? cur.total : cur.consumption) || 0);
        }, 0)
      );
      const rawTotalConsumption = data?.reduce((acc, cur) => {
          return acc + ((isDc ? cur.total : cur.consumption) || 0);
        }, 0);
      const totalConsumption = isDc
        ? formatDataConsumptionTotal(rawTotalConsumption)
        : Math.floor(rawTotalConsumption);

      return {
        actionType: isDc
          ? 'GLOBAL_DATA_TELEMETRY_OVERVIEW'
          : 'GLOBAL_TELEMETRY_OVERVIEW',
        deviceIds,
        deviceType: swtName.toUpperCase(),
        data: {
          startDate,
          endDate,
          isDataConsumption: isDc,
          totalUsageHours,
          totalConsumption,
          data: {
            deviceId: machine,
            usageHours: totalUsageHours,
            consumption: totalConsumption,
          },
        },
      };
    };

    if (selectedOne !== 'select graph type' && (isRealTimeGraph || hasDateRange)) {
      if (isSelected[1]) {
        const categories =
          swtName === 'tgs'
            ? [
                'gas_graph',
                'gas_enclosure_graph',
                'outside_graph',
                'gas_snow_graph',
                'gas_wind_graph',
              ]
            : [
                'heater_graph',
                'enclosure_graph',
                'outside_graph',
                'snow_graph',
                'wind_graph',
              ];
        const promises = categories.map((category) =>
          getTelemetryService({ deviceIds, startDate, endDate, category })
        );
        requests.push(
          Promise.all(promises).then((res) => {
            res.forEach(({ data, unit }, index) => {
              handleTelemetryData(data, unit, categories[index]);
            });
          })
        );
      }

      if (isSelected[2]) {
        const category = swtName === 'tgs' ? 'fuel' : 'energy';
        requests.push(
          getTelemetryService({ deviceIds, startDate, endDate, category }).then(
            ({ data, unit }) => {
              handleTelemetryData(data, unit, category);
              setAuditLogData(getAuditLogData(data[0]?.data, false));
            }
          )
        );
      }

      if (isSelected[3]) {
        const inhandIds = [
          locations[swtName][location]?.devices[machine]?.inhand_id,
        ];
        if (inhandIds[0]) {
          requests.push(
            getDataConsumptionService({ inhandIds, startDate, endDate }).then(
              (res) => {
                const newData = {
                  location,
                  machine,
                  graphType: 'dataConsumptionGraphData',
                  // Stored as the raw byte count the API reports. BarGraph
                  // picks one unit for the whole series and converts there,
                  // which it cannot do if the value is already scaled.
                  data: Object.values(res)[0]
                    .filter((el) => el.start_date)
                    .map((el) => {
                      return [el.start_date, el.total];
                    }),
                };
                if (swtName === 'ess') {
                  dispatch(handleEssGraphDate(newData));
                } else if (swtName === 'tgs') {
                  dispatch(handleTgsGraphDate(newData));
                } else if (swtName === 'tes') {
                  dispatch(handleTesGraphDate(newData));
                }

                setAuditLogData(getAuditLogData(Object.values(res)[0], true));
              }
            )
          );
        }
      }

      if (isSelected[0]) {
        setIsReadyToRender(false);
      } else {
        setIsReadyToRender(true);
      }

      setIsReportReady(true);
    }

    return Promise.all(requests);
  };


  const handleClear = () => {
    setDate({
      start: { date: null, time: null },
      end: { date: null, time: null },
    });
    setDisplayedRange(null);
    drilldown.reset();
  };

  const handleClose = () => {
    setOpenDateSelect(false);
  };

  const handleSetNewSchedule = (data) => {
    setDate(data);
    setOpenDateSelect(false);
  };

  const handleSelectButton = (btn) => {
    if (btn === 'clear') {

      setIsSelected([false, false, false, false]);
    } else if (btn === 'apply') {

      if (isSelected[0]) {
        setSelectedOne(graphSelection[0]);
      } else if (isSelected[1]) {
        setSelectedOne(graphSelection[1]);
      } else if (isSelected[2]) {
        setSelectedOne(graphSelection[2]);
      } else if (isSelected[3]) {
        setSelectedOne(graphSelection[3]);
      }

      setOpenGraphSelect(false);
    }
  };

  const handleGraphSelect = (idx) => {
    if (idx === 0) {
      setIsSelected([true, false, false, false]);
    } else if (idx === 1) {
      setIsSelected([false, true, false, false]);
    } else if (idx === 2) {
      setIsSelected([false, false, true, false]);
    } else if (idx === 3) {
      setIsSelected([false, false, false, true]);
    }
  };

  return (
    <Wrapper positionRelative={openDateSelect}>
      {/* controls stay out of the exported report, as on the telemetry page */}
      <SectionSelect className='no-print'>
        <SelectHole>
          <GraphAndDateWrapper>
            <SectionGraphSelect>
              <SelectSpan>select graph</SelectSpan>
              <GraphSelectWrapper openGraphSelect={openGraphSelect}>
                <SelectedOneAndArrowWrapper openGraphSelect={openGraphSelect}>
                  <GraphSelectTop>
                    <SelectSpan text={true}>{selectedOne}</SelectSpan>
                  </GraphSelectTop>
                  <ArrowImg
                    src={'/images/masterCtr-select-btn.svg'}
                    onClick={() => {
                      setOpenDateSelect(false);
                      setOpenGraphSelect(!openGraphSelect);
                    }}
                  />
                </SelectedOneAndArrowWrapper>
                {openGraphSelect && (
                  <>
                    <SectionSelectOption>
                      {graphSelection.map((option, idx) => (
                        <SelectOptions
                          onClick={() => handleGraphSelect(idx)}
                          key={idx}
                        >
                          <RadioButton>
                            <RadioIndicator
                              isSelected={isSelected[idx]}
                            ></RadioIndicator>
                          </RadioButton>
                          <Option>{option}</Option>
                        </SelectOptions>
                      ))}
                    </SectionSelectOption>
                    <SectionSelectButton>
                      {selectButtons.map((button, idx) => (
                        <ButtonWrapper
                          key={idx}
                          onClick={() => handleSelectButton(button)}
                        >
                          <ButtonHole>
                            <ButtonInner>
                              <ButtonTop>{button}</ButtonTop>
                            </ButtonInner>
                          </ButtonHole>
                        </ButtonWrapper>
                      ))}
                    </SectionSelectButton>
                  </>
                )}
              </GraphSelectWrapper>
            </SectionGraphSelect>

            <SectionDateSelect>
              <TitleWrapper>
                <SelectSpan>select start date</SelectSpan>
                <SelectSpan>select end date</SelectSpan>
              </TitleWrapper>
              <SelectDataWrapper disabled={isSelected[0]}>
                <SelectDateTop>
                  <SelectSpan>
                    {isSelected[0] ? '-----------------' : displayStart}
                  </SelectSpan>
                </SelectDateTop>
                <SelectDateTop>
                  <SelectSpan>
                    {isSelected[0] ? '-----------------' : displayEnd}
                  </SelectSpan>
                </SelectDateTop>
                <ArrowImg
                  disabled={isSelected[0]}
                  src={'/images/masterCtr-select-btn.svg'}
                  onClick={() => {
                    setOpenGraphSelect(false);
                    // the real time graph has no date range to pick
                    if (isSelected[0]) {
                      setOpenDateSelect(false);
                      handleMessage('realTimeGraph');
                      return;
                    }
                    setOpenDateSelect(!openDateSelect);
                  }}
                />
              </SelectDataWrapper>
            </SectionDateSelect>
          </GraphAndDateWrapper>

          <SectionButton>
            <SearchButton onClick={handleSearchButton}>
              <SearchButtonHole>
                <SearchButtonTop>
                  <SelectSpan>search</SelectSpan>
                </SearchButtonTop>
              </SearchButtonHole>
            </SearchButton>
          </SectionButton>
        </SelectHole>
      </SectionSelect>

      <SectionInfo>
        <SectionConsumption>
          <DisplayConsumptionBox
            location={location}
            machine={machine}
            swtName={swtName}
          />
        </SectionConsumption>

        <SectionSwitchTitle>
          <DisplayGraphInfoTitle
            location={location}
            machine={machine}
            swtName={swtName}
            start={isReadyToRender ? displayStart : '-------------'}
            end={isReadyToRender ? displayEnd : '-------------'}
            graph={
              isSelected[0]
                ? graphSelection[0]
                : isSelected[1]
                ? graphSelection[1]
                : isSelected[2]
                ? graphSelection[2]
                : isSelected[3]
                ? graphSelection[3]
                : '------- vs -------'
            }
          />
        </SectionSwitchTitle>
        <SectionTimer>
          <DisplayFaultsTimer
            location={location}
            machine={machine}
            swtName={swtName}
          />
        </SectionTimer>
      </SectionInfo>

      <SectionChart>
        <ChartContainer
          isReadyToRender={isReadyToRender}
          chartOption={isSelected}
          swtName={swtName}
          graph={selectedOne}
          location={location}
          machine={machine}
          onPointClick={handlePointClick}
          canDrillOut={drilldown.canDrillOut}
          onDrillOut={drilldown.drillOut}
        />
      </SectionChart>

      <SectionLegend>
        <DisplayLegend
          isReadyToRender={isReadyToRender}
          // both temperature graphs are line charts, so they share the ht/et/ot
          // legend; only the two consumption graphs get the single-series one
          option={isSelected[0] || isSelected[1]}
          swtName={swtName}
          graph={isSelected[3] ? 'data' : 'energy'}
        />
      </SectionLegend>

      {openDateSelect && !isSelected[0] && (
        <ScheduleCalendarWrapper>
          <ScheduleCalendar
            handleScheduler={handleSetNewSchedule}
            handleClose={handleClose}
            handleClear={handleClear}
            scheduleList={date}
            noTimePicker={true}
            isToSelectPastDates={true}
          />
        </ScheduleCalendarWrapper>
      )}
    </Wrapper>
  );
};

export default GraphMain;

const Wrapper = styled.div`
  width: 100%;
  height: 488px;

  ${flexDirectionColumn};

  ${(p) =>
    p.positionRelative &&
    css`
      position: relative;
    `}
`;
const SectionSelect = styled.section`
  width: 743px;
  height: 59px;
  border-radius: 30px;

  ${layerB};
  ${flexBoxCenter};
`;

const SelectHole = styled.div`
  width: 738px;
  height: 55px;
  border-radius: 30px;

  ${layerA180Deg};
  ${justifyContentSpaceBetween};
  padding: 0 10px 0 4px;
`;
const GraphAndDateWrapper = styled.div`
  width: 643px;
  height: 47px;
  border-radius: 30px;

  ${layerA};
  ${justifyContentSpaceBetween};
  padding: 0 8px 0 8px;
`;

const SectionGraphSelect = styled.section`
  height: 100%;
  ${flexDirectionColumn};
  padding: 1px 0;
  z-index: 100;
`;

const GraphSelectWrapper = styled.div`
  width: 189px;
  height: 33px;
  border-radius: 25px;

  ${layerA180Deg};
  ${flexBoxCenter};

  ${(p) =>
    p.openGraphSelect &&
    css`
      height: auto;
      flex-direction: column;
      align-items: space;
      padding: 3px 2px 2px 2px;
      border-radius: 15px;
      margin-top: 2px;
    `}
`;

const SelectedOneAndArrowWrapper = styled.div`
  width: 189px;
  ${justifyContentSpaceBetween};
  padding: 0 8px 0 4px;
  ${(p) =>
    p.openGraphSelect &&
    css`
      padding: 0 9px 0 5px;
      margin-bottom: 4px;
    `}
`;

const GraphSelectTop = styled.div`
  width: 152px;
  height: 25px;
  border-radius: 18px;

  ${layerA};
  ${flexBoxCenter};
`;
const ArrowImg = styled.img`
  margin-top: 2px;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.4;
    `}
`;

const SelectSpan = styled.span`
  font-size: 8px;

  ${(p) =>
    p.text &&
    css`
      text-transform: capitalize;
    `}
`;

const SectionSelectOption = styled.section`
  width: 100%;
  border-radius: 14px;

  ${layerBDark};
  padding: 2px;

  margin-bottom: 2px;
`;

const SelectOptions = styled.button`
  width: 100%;
  height: 24px;
  border-radius: 16px;
  border: 1px solid #233a54;

  ${justifyContentFlexStart};

  padding: 0 2rem;
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }

  :hover {
    ${layerA};
  }
`;
const RadioButton = styled.div`
  width: 18rem;
  height: 18rem;
  border: 1px solid #95ff45;
  border-radius: 50%;
  ${flexBoxCenter}
`;
const RadioIndicator = styled.div`
  width: 12rem;
  height: 12rem;
  border-radius: 50%;

  background-color: ${(p) => (p.isSelected ? '#95ff45' : 'none')};
`;

const Option = styled.span`
  font-size: 8px;
  margin-left: 5px;
  text-transform: capitalize;
`;

const SectionSelectButton = styled.section`
  width: 100%;
  ${justifyContentSpaceBetween};
`;

const ButtonWrapper = styled.button`
  width: 84px;
  height: 27px;
  border-radius: 18px;

  ${layerBDark};
  ${flexBoxCenter};
`;
const ButtonHole = styled.div`
  width: 82px;
  height: 25px;
  border-radius: 25px;

  ${layerA180Deg};
  ${flexBoxCenter}
`;
const ButtonInner = styled.div`
  width: 74px;
  height: 17px;
  border-radius: 18px;

  ${layerCLighter};
  ${flexBoxCenter};
`;
const ButtonTop = styled.div`
  width: 72px;
  height: 15px;
  border-radius: 25px;

  ${layerA180Deg};
  font-size: 10px;
  ${flexBoxCenter}
`;

const SectionDateSelect = styled.section`
  height: 100%;

  ${flexDirectionColumn};
  padding: 1px 0;
`;
const TitleWrapper = styled.div`
  width: 423px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 0 30px 0 0;
`;

const SelectDataWrapper = styled.div`
  width: 423px;
  height: 33px;
  border-radius: 20px;

  ${layerA180Deg};
  ${justifyContentSpaceBetween};
  padding: 0 10px 0 5px;

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
    `}
`;
const SelectDateTop = styled.div`
  width: 185px;
  height: 25px;
  border-radius: 18px;

  ${layerA};
  ${flexBoxCenter};
`;

const SectionButton = styled.section`
  width: 63px;
  height: 29px;
  border-radius: 27px;

  ${layerB};
  ${flexBoxCenter}
`;
const SearchButton = styled.button`
  width: 61px;
  height: 27px;
  border-radius: 25px;

  ${layerA180Deg};
  ${flexBoxCenter}
`;

const SearchButtonHole = styled.div`
  width: 55px;
  height: 21px;
  border-radius: 20px;

  ${layerB};
  ${flexBoxCenter};
`;
const SearchButtonTop = styled.div`
  width: 53px;
  height: 19px;
  border-radius: 25px;

  ${layerA180Deg};
  ${flexBoxCenter}
`;

const SectionInfo = styled.section`
  width: 743px;
  ${justifyContentSpaceBetween};
`;
const SectionConsumption = styled.section``;
const SectionSwitchTitle = styled.section``;
const SectionTimer = styled.section``;

const SectionChart = styled.section``;

const SectionLegend = styled.section`
  width: 743px;
`;

const ScheduleCalendarWrapper = styled.div`
  width: 120%;
  height: 100%;
  ${flexBoxCenter};

  position: absolute;
  top: -80px;
  left: -120px;
  z-index: 100;
`;
